import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import parse from 'html-react-parser'
import { useNavigate, useParams } from 'react-router-dom'
import API from '../../../services/API'
import { UserContext } from '../../../App'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import { TabLabel } from '../../fleetEquipment/vehicles/VehicleComponents/SectionKit'
import SafetyAlertDrawer from './SafetyAlertDrawer'
import { plainText } from './safetyAlertHelpers'
// MUI
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  InputAdornment,
  LinearProgress,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined'
import TaskAltIcon from '@mui/icons-material/TaskAlt'

const MHRA_ALERTS_URL = 'https://www.gov.uk/drug-device-alerts'

const PendingAlertCard = ({ alert, onOpen, onDismiss }) => (
  <Card
    variant="outlined"
    onClick={onOpen}
    sx={{
      p: 1.75,
      borderRadius: 2.5,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      minWidth: 0,
      borderLeft: (t) => `4px solid ${t.palette.warning.main}`,
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      '&:hover': { borderColor: 'warning.main', boxShadow: 2 },
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Chip
        size="small"
        label={alert.source ?? 'MHRA'}
        sx={{ borderRadius: 1, fontWeight: 600 }}
      />
      <Tooltip title={`Published ${moment(alert.updated).format('Do MMM YYYY')}`}>
        <Typography variant="caption" color="text.secondary">
          {moment(alert.updated).fromNow()}
        </Typography>
      </Tooltip>
    </Stack>
    <Typography variant="body1" fontWeight={700} lineHeight={1.35}>
      {parse(alert.title ?? '')}
    </Typography>
    {alert.summary && (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {plainText(alert.summary)}
      </Typography>
    )}
    <Stack direction="row" spacing={1} mt="auto" pt={0.5}>
      <Button
        size="small"
        variant="contained"
        color="success"
        startIcon={<CheckCircleOutlineIcon />}
        onClick={(e) => {
          e.stopPropagation()
          onOpen()
        }}
        sx={{ borderRadius: 2, textTransform: 'none' }}
      >
        Review
      </Button>
      <Button
        size="small"
        color="error"
        startIcon={<DoNotDisturbAltIcon />}
        onClick={(e) => {
          e.stopPropagation()
          onDismiss()
        }}
        sx={{ borderRadius: 2, textTransform: 'none' }}
      >
        Not relevant
      </Button>
    </Stack>
  </Card>
)

const SafetyAlerts = () => {
  const { user } = useContext(UserContext)
  const canAction =
    user?.employee?.teams?.includes('Governance') || user?.security?.isSiteAdmin
  const { id: selectedId } = useParams()
  const navigate = useNavigate()

  const [pending, setPending] = useState([])
  const [accepted, setAccepted] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [tab, setTab] = useState('pending')
  const [search, setSearch] = useState('')
  const [snack, setSnack] = useState(null) // { severity, message, undo? }

  const loadPending = () =>
    API.get('/safety-alerts').then((res) => {
      if (res.status !== 200) throw new Error()
      setPending(res.data)
    })

  const loadAccepted = () =>
    API.get('/reviewed-safety-alerts').then((res) => {
      if (res.status !== 200) throw new Error()
      setAccepted(res.data)
    })

  useEffect(() => {
    if (!canAction) {
      setLoading(false)
      return
    }
    Promise.all([loadPending(), loadAccepted()])
      .then(() => setFetchError(false))
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [canAction])

  const openAlert = (id) => navigate(`/safety-alerts/${id}`)
  const closeAlert = () => navigate('/safety-alerts')

  const setDecision = (alert, accepted) =>
    API.put('/safety-alerts', { alertID: alert._id, accepted })

  const undoDismiss = (alert) => {
    setSnack(null)
    setDecision(alert, null).then((res) => {
      if (res.status === 200) {
        setPending((prev) =>
          [...prev.filter((a) => a._id !== alert._id), res.data].sort(
            (a, b) => new Date(b.updated) - new Date(a.updated),
          ),
        )
      } else {
        setSnack({ severity: 'error', message: 'Unable to restore the alert' })
      }
    })
  }

  const dismiss = (alert) => {
    setDecision(alert, false)
      .then((res) => {
        if (res.status !== 200) throw new Error()
        setPending((prev) => prev.filter((a) => a._id !== alert._id))
        if (selectedId === alert._id) closeAlert()
        setSnack({
          severity: 'info',
          message: 'Alert dismissed as not relevant',
          undo: () => undoDismiss(alert),
        })
      })
      .catch(() =>
        setSnack({ severity: 'error', message: 'Unable to dismiss the alert' }),
      )
  }

  const handleChanged = (alert, change) => {
    if (change === 'accepted') {
      setPending((prev) => prev.filter((a) => a._id !== alert._id))
      setSnack({ severity: 'success', message: 'Alert accepted' })
    }
    if (canAction) loadAccepted().catch(() => {})
  }

  const filteredAccepted = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return accepted
    return accepted.filter((a) =>
      [
        plainText(a.title),
        a.actionTaken,
        a.source,
        a.actionedBy?.firstName,
        a.actionedBy?.lastName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term),
    )
  }, [accepted, search])

  const columns = [
    {
      headerName: 'Published',
      field: 'updated',
      minWidth: 115,
      valueGetter: (value) => (value ? new Date(value) : null),
      renderCell: ({ row }) => (
        <Typography variant="body2">
          {moment(row.updated).format('DD/MM/YYYY')}
        </Typography>
      ),
    },
    {
      headerName: 'Alert',
      field: 'title',
      flex: 1.2,
      minWidth: 240,
      renderCell: ({ row }) => (
        <Box minWidth={0} py={1}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {plainText(row.title)}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {row.source}
          </Typography>
        </Box>
      ),
    },
    {
      headerName: 'Action taken',
      field: 'actionTaken',
      flex: 1,
      minWidth: 200,
      renderCell: ({ row }) => (
        <Tooltip title={row.actionTaken ?? ''}>
          <Typography variant="body2" noWrap>
            {row.actionTaken || '—'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      headerName: 'Follow-up',
      field: 'followUp',
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => {
        const tasks = row.tasks ?? []
        const open = tasks.filter((t) => t.taskStatus !== 'Completed').length
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" height="100%">
            {tasks.length > 0 && (
              <Tooltip title={`${open} open of ${tasks.length} task${tasks.length === 1 ? '' : 's'}`}>
                <Chip
                  size="small"
                  icon={<TaskAltIcon />}
                  label={open ? `${open} open` : 'Done'}
                  color={open ? 'warning' : 'success'}
                  variant="outlined"
                  sx={{ borderRadius: 1, fontWeight: 600 }}
                />
              </Tooltip>
            )}
            {row.announcement && (
              <Tooltip title="Published as an announcement">
                <CampaignOutlinedIcon fontSize="small" color="action" />
              </Tooltip>
            )}
            {!tasks.length && !row.announcement && (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            )}
          </Stack>
        )
      },
    },
    {
      headerName: 'Accepted by',
      field: 'actionedBy',
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) =>
        row.actionedBy ? (
          <Stack direction="row" spacing={1} alignItems="center" height="100%" minWidth={0}>
            <Avatar
              src={row.actionedBy.photograph}
              sx={{ width: 26, height: 26, fontSize: '0.75rem' }}
            >
              {row.actionedBy.firstName?.[0]}
            </Avatar>
            <Box minWidth={0}>
              <Typography variant="body2" noWrap>
                {row.actionedBy.firstName} {row.actionedBy.lastName}
              </Typography>
              {row.actionedAt && (
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {moment(row.actionedAt).format('DD MMM YYYY')}
                </Typography>
              )}
            </Box>
          </Stack>
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        ),
    },
  ]

  return (
    <Stack gap={1.5}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        gap={1}
      >
        <Box>
          <Typography variant="title2" component="div">
            Safety Alerts
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            MHRA drug and device alerts, checked every night
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={() => window.open(MHRA_ALERTS_URL, '_blank', 'noopener')}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          MHRA alerts on GOV.UK
        </Button>
      </Stack>

      {!canAction && (
        <Alert severity="info">
          Safety alerts are reviewed by the Governance team.
        </Alert>
      )}

      {canAction && (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
              <Tab
                value="pending"
                label={
                  <TabLabel
                    label="To review"
                    count={loading ? undefined : pending.length}
                    color={pending.length ? 'warning' : 'default'}
                  />
                }
                sx={{ textTransform: 'none' }}
              />
              <Tab
                value="accepted"
                label={
                  <TabLabel
                    label="Accepted"
                    count={loading ? undefined : accepted.length}
                  />
                }
                sx={{ textTransform: 'none' }}
              />
            </Tabs>
          </Box>

          {loading && <LinearProgress />}
          {fetchError && (
            <Alert severity="error">
              Unable to load safety alerts. Please try again later.
            </Alert>
          )}

          {!loading && tab === 'pending' && (
            pending.length === 0 ? (
              <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
                You're all caught up — no new safety alerts to review.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Accept an alert that affects your service and record what
                  you did. Alerts dismissed as not relevant are removed.
                </Typography>
                <Box
                  display="grid"
                  gap={1.5}
                  gridTemplateColumns={{
                    xs: '1fr',
                    md: 'repeat(2, minmax(0, 1fr))',
                    xl: 'repeat(3, minmax(0, 1fr))',
                  }}
                >
                  {pending.map((alert) => (
                    <PendingAlertCard
                      key={alert._id}
                      alert={alert}
                      onOpen={() => openAlert(alert._id)}
                      onDismiss={() => dismiss(alert)}
                    />
                  ))}
                </Box>
              </>
            )
          )}

          {!loading && tab === 'accepted' && (
            <>
              <TextField
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                placeholder="Search alerts, actions taken, reviewers…"
                sx={{ maxWidth: { sm: 420 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <CustomDataGrid
                norowsoverlay={
                  <Alert severity="info">
                    {accepted.length === 0
                      ? 'No alerts have been accepted yet.'
                      : 'No accepted alerts match your search.'}
                  </Alert>
                }
                rows={filteredAccepted}
                columns={columns}
                getRowId={(row) => row._id}
                getRowHeight={() => 'auto'}
                pagination
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                disableRowSelectionOnClick
                onRowClick={({ row }) => openAlert(row._id)}
                sx={{
                  '.MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
                  '.MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
                }}
                slots={{ toolbar: () => null }}
              />
            </>
          )}
        </>
      )}

      <SafetyAlertDrawer
        alertId={selectedId ?? null}
        onClose={closeAlert}
        onChanged={handleChanged}
        onDismiss={dismiss}
        canAction={canAction}
      />

      <Snackbar
        open={!!snack}
        autoHideDuration={snack?.undo ? 8000 : 4000}
        onClose={(e, reason) => reason !== 'clickaway' && setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            variant="filled"
            onClose={() => setSnack(null)}
            action={
              snack.undo && (
                <Button color="inherit" size="small" onClick={snack.undo}>
                  Undo
                </Button>
              )
            }
          >
            {snack.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Stack>
  )
}

export default SafetyAlerts
